import { useEffect, useRef, useState } from 'react';

/**
 * @deprecated
 * Use useAsyncInterval instead
 *
 * Retry mechanism for async functions
 *
 * A non-zero delay value must be set to activate the retry mechanism.
 * Setting a _delay value on initialization will immediately trigger the retry mechanism.
 * Updating the delay value resets the retry mechanism.
 *
 * Overlapping function calls are not allowed i.e. retries are ignored until any active function is resolved. Ignored
 * function calls do not increase the retry count. Since async functions can return arbitrarily, this function does not
 * guarantee equally timed retries.
 *
 * @param callback {function} Async function to be called
 * @param _delay {number} Value of delay in seconds. Setting this to a non-zero value will immediately trigger the interval
 * @param options {Object} Other configurable options
 * @param options.onClearInterval {function} Callback function every time retry mechanism is reset
 * @param options.maxRetry {number} Maximum number of retries allowed
 */
export function useAsyncIntervalDeprecated(callback, _delay = 0, options = {}) {
  const { onClearInterval, maxRetry } = options;
  const savedCallback = useRef(async () => {});
  const loading = useRef(false);

  // track how many times we've resolved from the callback
  const [retryCount, setRetryCount] = useState(0);
  const resetRetryCount = () => setRetryCount(0);

  // setting a non-zero value with start polling
  const [delay, setDelay] = useState(_delay);
  const clearDelay = () => setDelay(0);


  function reset() {
    loading.current = false;
    resetRetryCount();
    clearDelay();
  }

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // check if we've already attempted the max retries before retrying the function call
  useEffect(() => {
    if (maxRetry) {
      if (retryCount >= maxRetry) {
        reset();
      }
    }
  }, [retryCount]);

  // Set up the interval
  useEffect(() => {
    // interval callback
    async function tick() {
      // execute callback only if previous async callback has been resolved
      if (!loading.current) {
        loading.current = true;
        try {
          await savedCallback.current();
        } finally {
          // increment resolved callback count
          setRetryCount(count => count + 1);
          loading.current = false;
        }
      }
    }
    // setup interval only if delay value exists
    if (delay) {
      const id = setInterval(tick, delay * 1000);
      // clear interval if delay value is changed
      return () => {
        clearInterval(id);
        reset();
        if (onClearInterval) onClearInterval();
      };
    }
    return () => {};
  }, [delay]);

  return {
    resetRetryCount,
    setDelay,
    clearDelay,
    reset,
    retryCount,
  };
}

/**
 *
 * Retry mechanism for async functions
 *
 * Callbacks are executed one at a time.
 *
 * @param callback {function} Async function to be called
 * @param delay {number} Value of delay in seconds
 * @param maxRetries {number} Max retries allowed
 * @param maxRetriesCallback {function=} Function to call if max retries limit reached
 * @param onCallbackError {function=} Function to call on callback error
 * @return {{retries: number, stop: stop, start: start}}
 */
export function buildAsyncInterval(callback, {
  delay, maxRetries, maxRetriesCallback, onCallbackError,
}) {
  let retries = 0;
  let intervalId;
  let isRunning = false;

  // stop any running interval
  function stop() {
    if (!intervalId) return;
    clearInterval(intervalId);
    isRunning = false;
  }

  // main process
  async function main() {
    // do not execute if another asynchronous callback has not finished executing
    if (isRunning || !intervalId) return;

    // check if maximum retry limit reached
    if (retries >= maxRetries) {
      // clear interval
      stop();
      // execute any max retries callback
      if (maxRetriesCallback) maxRetriesCallback();
      return;
    }

    // start execution of callback

    // prevent parallel callback executions
    isRunning = true;
    // increment retry count
    retries++;
    try {
      await callback({ stop });
    } catch (e) {
      // execute any error callback
      if (onCallbackError) onCallbackError(e);
    } finally {
      // allow next callback execution
      isRunning = false;
    }
  }

  function start() {
    if (intervalId) return;
    intervalId = setInterval(main, delay * 1000);
  }

  return {
    start,
    stop,
    retries,
  };
}

/**
 * React hook that wraps the buildAsyncInterval functionality
 *
 * @example
 * const interval = useAsyncInterval(waitForSuccessStatus, { delay: 2, maxRetries: 10, maxRetriesCallback: printCouldNotAchieveSuccessStatus}
 * useEffect(() => {
 *   // start interval
 *   interval.current.start();
 *   // always remember to stop interval during effect cleanup
 *   return () => {
 *     interval.current.stop();
 *   }
 * })
 *
 * @param callback {function} Async function to be called
 * @param delay {number} Value of delay in seconds
 * @param maxRetries {number} Max retries allowed
 * @param maxRetriesCallback {function=} Function to call if max retries limit reached
 * @param onCallbackError {function=} Function to call on callback error
 * @return {React.MutableRefObject<{retries: number, stop: stop, start: start}>}
 */
export default function useAsyncInterval(callback, {
  delay, maxRetries, maxRetriesCallback, onCallbackError,
}) {
  return useRef(buildAsyncInterval(callback, {
    delay, maxRetries, maxRetriesCallback, onCallbackError,
  }));
}
